/**
 * SearchPanel.tsx — Organizations V3 (HCO)
 * Search fields row: Org Name, City, State, ZIP, Org ID, NPI, TIN, Address,
 * and action buttons.
 * Uses react-hook-form 7 — fields are wired with {...register(name)}, same pattern
 * as ExUsProfessionalsV3/components/SearchPanel.tsx.
 */
import React from 'react';
import type { UseFormRegister, UseFormSetValue } from 'react-hook-form';
import styles from '../OrganizationsV3.module.css';
import hcoLookup from '../../../lookup/hco.json';

export interface SearchFormValues {
    orgName:  string;
    city:     string;
    state:    string;
    zip:      string;
    orgId:    string;
    npi:      string;
    tin:      string;
    address:  string;
    universe: string;
    orgType:  string;
    showAll:  string;
}

interface Props {
    register:   UseFormRegister<SearchFormValues>;
    setValue:   UseFormSetValue<SearchFormValues>;
    onSearch:   () => void;
    onClearAll: () => void;
    isLoading:  boolean;
}

// State options from hco.json searchParams STATE field
const stateParam = (hcoLookup as {
    searchParams: Array<{ column: string; type: string; options?: Record<string, string> }>;
}).searchParams.find(p => p.column === 'STATE');

const stateOptions: Array<{ value: string; label: string }> = stateParam?.options
    ? Object.entries(stateParam.options).map(([value, label]) => ({ value, label }))
    : [];

export default function SearchPanel({ register, onSearch, onClearAll, isLoading }: Props) {
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') onSearch();
    };

    return (
        <div className={styles.searchPanel} onKeyDown={handleKeyDown}>

            <div className={styles.searchField}>
                <label className={styles.fieldLabel}>Org Name</label>
                <input
                    className={styles.fieldInput}
                    {...register('orgName')}
                    autoComplete="off"
                    data-testid="field-orgName"
                />
            </div>

            <div className={styles.searchField}>
                <label className={styles.fieldLabel}>City</label>
                <input
                    className={styles.fieldInput}
                    {...register('city')}
                    autoComplete="off"
                    data-testid="field-city"
                />
            </div>

            <div className={styles.searchField}>
                <label className={styles.fieldLabel}>State</label>
                <select
                    className={styles.fieldSelect}
                    {...register('state')}
                    data-testid="field-state"
                >
                    <option value="">— All —</option>
                    {stateOptions.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
            </div>

            <div className={styles.searchField}>
                <label className={styles.fieldLabel}>ZIP</label>
                <input
                    className={styles.fieldInput}
                    style={{ minWidth: 80 }}
                    {...register('zip')}
                    autoComplete="off"
                    data-testid="field-zip"
                />
            </div>

            <div className={styles.searchField}>
                <label className={styles.fieldLabel}>Org ID</label>
                <input
                    className={styles.fieldInput}
                    style={{ minWidth: 90 }}
                    {...register('orgId')}
                    autoComplete="off"
                    data-testid="field-orgId"
                />
            </div>

            <div className={styles.searchField}>
                <label className={styles.fieldLabel}>NPI</label>
                <input
                    className={styles.fieldInput}
                    style={{ minWidth: 110 }}
                    {...register('npi')}
                    autoComplete="off"
                    data-testid="field-npi"
                />
            </div>

            <div className={styles.searchField}>
                <label className={styles.fieldLabel}>TIN</label>
                <input
                    className={styles.fieldInput}
                    style={{ minWidth: 100 }}
                    {...register('tin')}
                    autoComplete="off"
                    data-testid="field-tin"
                />
            </div>

            <div className={styles.searchField}>
                <label className={styles.fieldLabel}>Address</label>
                <input
                    className={styles.fieldInput}
                    {...register('address')}
                    autoComplete="off"
                    data-testid="field-address"
                />
            </div>

            <div className={styles.searchActions}>
                <button
                    type="button"
                    onClick={onSearch}
                    disabled={isLoading}
                    data-testid="btn-search"
                    style={{
                        background: '#fff',
                        color: '#3d3e8e',
                        border: '1px solid #fff',
                        borderRadius: 3,
                        padding: '6px 18px',
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        opacity: isLoading ? 0.6 : 1,
                    }}
                >
                    🔍 SEARCH
                </button>
                <button
                    type="button"
                    onClick={onClearAll}
                    disabled={isLoading}
                    data-testid="btn-clearAll"
                    style={{
                        background: 'transparent',
                        color: '#fff',
                        border: '1px solid #fff',
                        borderRadius: 3,
                        padding: '6px 14px',
                        fontSize: 13,
                        cursor: 'pointer',
                    }}
                >
                    CLEAR ALL
                </button>
            </div>

        </div>
    );
}
